/**
 * HIMALIROCK — Premium Cart Drawer
 * Vanilla JS AJAX cart controller. No dependencies.
 * Talks to /cart.js, /cart/add.js, /cart/change.js, /cart/update.js
 */

(function () {
  'use strict';

  var SELECTORS = {
    root: '[data-premium-cart]',
    overlay: '[data-pc-overlay]',
    panel: '[data-pc-panel]',
    close: '[data-pc-close]',
    body: '[data-pc-body]',
    items: '[data-pc-items]',
    item: '[data-pc-item]',
    empty: '[data-pc-empty]',
    footer: '[data-pc-footer]',
    count: '[data-pc-count]',
    subtotal: '[data-pc-subtotal]',
    stepper: '[data-pc-stepper]',
    decrease: '[data-pc-decrease]',
    increase: '[data-pc-increase]',
    qtyInput: '[data-pc-qty-input]',
    remove: '[data-pc-remove]',
    linePrice: '[data-pc-line-price]',
    checkout: '[data-pc-checkout]',
    shippingNotice: '[data-pc-shipping-notice]',
    shippingText: '[data-pc-shipping-text]',
    shippingFill: '[data-pc-shipping-fill]',
    itemTemplate: '[data-pc-item-template]',
    openTrigger: '[data-pc-open], [data-premium-cart-open]'
  };

  var FOCUSABLE = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

  function PremiumCart(root) {
    this.root = root;
    this.panel = root.querySelector(SELECTORS.panel);
    this.overlay = root.querySelector(SELECTORS.overlay);
    this.itemsList = root.querySelector(SELECTORS.items);
    this.emptyState = root.querySelector(SELECTORS.empty);
    this.footer = root.querySelector(SELECTORS.footer);
    this.countEl = root.querySelector(SELECTORS.count);
    this.subtotalEl = root.querySelector(SELECTORS.subtotal);
    this.checkoutBtn = root.querySelector(SELECTORS.checkout);
    this.shippingNotice = root.querySelector(SELECTORS.shippingNotice);
    this.shippingText = root.querySelector(SELECTORS.shippingText);
    this.shippingFill = root.querySelector(SELECTORS.shippingFill);
    this.itemTemplate = document.getElementById(
      root.querySelector(SELECTORS.itemTemplate) ? root.querySelector(SELECTORS.itemTemplate).id : ''
    );

    this.freeShippingEnabled = root.dataset.freeShippingEnabled === 'true';
    this.freeShippingThreshold = parseInt(root.dataset.freeShippingThreshold, 10) || 0;
    this.moneyFormat = root.dataset.moneyFormat || '${{amount}}';

    this.isOpen = false;
    this.lastFocusedEl = null;
    this.updateTimers = {};

    this.bindEvents();
    this.syncFromServer();
  }

  /* ---------------------------------- Utilities ---------------------------------- */

  PremiumCart.prototype.formatMoney = function (cents) {
    var value = (cents / 100).toFixed(2);
    var parts = value.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var formatted = parts.join('.');
    if (this.moneyFormat.indexOf('{{amount}}') !== -1) {
      return this.moneyFormat.replace('{{amount}}', formatted);
    }
    return '$' + formatted;
  };

  PremiumCart.prototype.trapFocus = function (event) {
    if (event.key !== 'Tab') return;
    var focusable = Array.prototype.slice.call(this.panel.querySelectorAll(FOCUSABLE)).filter(function (el) {
      return el.offsetParent !== null;
    });
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  /* ---------------------------------- Open / Close ---------------------------------- */

  PremiumCart.prototype.open = function () {
    if (this.isOpen) return;
    this.isOpen = true;
    this.lastFocusedEl = document.activeElement;
    this.root.classList.add('is-open');
    this.root.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('pc-drawer-open');

    var closeBtn = this.panel.querySelector(SELECTORS.close);
    window.setTimeout(function () {
      if (closeBtn) closeBtn.focus();
    }, 50);

    document.addEventListener('keydown', this.boundKeydown);
  };

  PremiumCart.prototype.close = function () {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.root.classList.remove('is-open');
    this.root.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('pc-drawer-open');
    document.removeEventListener('keydown', this.boundKeydown);

    if (this.lastFocusedEl && typeof this.lastFocusedEl.focus === 'function') {
      this.lastFocusedEl.focus();
    }
  };

  /* ---------------------------------- Cart API ---------------------------------- */

  PremiumCart.prototype.fetchCart = function () {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (res) {
      return res.json();
    });
  };

  PremiumCart.prototype.syncFromServer = function () {
    var self = this;
    this.fetchCart().then(function (cart) {
      self.render(cart);
    });
  };

  PremiumCart.prototype.addItem = function (formData) {
    var self = this;
    this.setBusy(true);
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: formData
    })
      .then(function (res) {
        return res.json().then(function (data) {
          if (!res.ok) throw data;
          return data;
        });
      })
      .then(function () {
        return self.fetchCart();
      })
      .then(function (cart) {
        self.render(cart);
        self.open();
        self.setBusy(false);
      })
      .catch(function (err) {
        self.setBusy(false);
        self.notifyError(err && err.description ? err.description : 'Could not add item to cart.');
      });
  };

  PremiumCart.prototype.changeLine = function (key, quantity, itemEl) {
    var self = this;
    if (itemEl) itemEl.classList.add('is-updating');

    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    })
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        if (quantity === 0 && itemEl) {
          itemEl.classList.add('is-removing');
          window.setTimeout(function () {
            self.render(cart);
          }, 300);
        } else {
          self.render(cart);
        }
      })
      .catch(function () {
        if (itemEl) itemEl.classList.remove('is-updating');
        self.notifyError('Could not update your cart. Please try again.');
      });
  };

  PremiumCart.prototype.queueChange = function (key, quantity, itemEl) {
    var self = this;
    if (this.updateTimers[key]) window.clearTimeout(this.updateTimers[key]);
    this.updateTimers[key] = window.setTimeout(function () {
      self.changeLine(key, quantity, itemEl);
      delete self.updateTimers[key];
    }, 250);
  };

  /* ---------------------------------- Render ---------------------------------- */

  PremiumCart.prototype.render = function (cart) {
    this.renderCount(cart.item_count);
    this.renderSubtotal(cart.total_price);
    this.renderItems(cart);
    this.renderShipping(cart.total_price);
    this.renderEmptyState(cart.item_count);
    this.updateHeaderBubble(cart.item_count);
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
  };

  PremiumCart.prototype.renderCount = function (count) {
    if (!this.countEl) return;
    this.countEl.textContent = count;
    this.countEl.classList.add('is-bumped');
    window.setTimeout(function (el) {
      el.classList.remove('is-bumped');
    }, 220, this.countEl);
  };

  PremiumCart.prototype.renderSubtotal = function (totalCents) {
    if (this.subtotalEl) this.subtotalEl.textContent = this.formatMoney(totalCents);
  };

  PremiumCart.prototype.renderEmptyState = function (count) {
    // Single source of truth: one attribute drives both the empty illustration
    // and the items/footer visibility in CSS, so they can never show together.
    this.root.setAttribute('data-cart-state', count > 0 ? 'filled' : 'empty');
  };

  PremiumCart.prototype.renderShipping = function (totalCents) {
    if (!this.freeShippingEnabled || !this.shippingNotice) return;
    this.shippingNotice.hidden = false;
    var remaining = this.freeShippingThreshold - totalCents;
    var pct = Math.min(100, Math.max(0, (totalCents / this.freeShippingThreshold) * 100));
    if (!this.freeShippingThreshold) {
      this.shippingNotice.hidden = true;
      return;
    }
    if (remaining > 0) {
      this.shippingText.textContent = 'Add ' + this.formatMoney(remaining) + ' more for free shipping';
    } else {
      this.shippingText.textContent = "You've unlocked free shipping";
    }
    if (this.shippingFill) this.shippingFill.style.width = pct + '%';
  };

  PremiumCart.prototype.renderItems = function (cart) {
    var self = this;
    if (!this.itemsList) return;

    var existingKeys = {};
    Array.prototype.forEach.call(this.itemsList.querySelectorAll(SELECTORS.item), function (el) {
      existingKeys[el.dataset.key] = el;
    });

    var newKeys = {};
    cart.items.forEach(function (item, index) {
      newKeys[item.key] = true;
      var el = existingKeys[item.key];
      if (el) {
        self.updateItemEl(el, item);
      } else {
        el = self.buildItemEl(item);
        self.itemsList.appendChild(el);
      }
      self.itemsList.appendChild(el);
    });

    Object.keys(existingKeys).forEach(function (key) {
      if (!newKeys[key]) {
        existingKeys[key].remove();
      }
    });
  };

  PremiumCart.prototype.buildItemEl = function (item) {
    var template = this.root.parentElement.querySelector('template[data-pc-item-template]') ||
      document.querySelector('template[data-pc-item-template]');
    var node = template.content.firstElementChild.cloneNode(true);
    this.updateItemEl(node, item);
    return node;
  };

  PremiumCart.prototype.updateItemEl = function (el, item) {
    el.dataset.key = item.key;
    el.classList.remove('is-updating', 'is-removing');

    var img = el.querySelector('.pc-item__image');
    if (img && item.image) {
      img.src = item.image.replace(/(\.[a-zA-Z]{3,4})(\?|$)/, '_300x$1$2');
      img.alt = item.title || '';
    }

    var vendor = el.querySelector('[data-pc-item-vendor]') || el.querySelector('.pc-item__vendor');
    if (vendor) vendor.textContent = item.vendor || '';

    var title = el.querySelector('[data-pc-item-title]') || el.querySelector('.pc-item__title');
    if (title) {
      title.textContent = item.product_title || item.title;
      title.setAttribute('href', item.url);
    }

    var variant = el.querySelector('[data-pc-item-variant]') || el.querySelector('.pc-item__variant');
    if (variant) variant.textContent = item.variant_title || '';

    var qtyInput = el.querySelector(SELECTORS.qtyInput);
    if (qtyInput) qtyInput.value = item.quantity;

    var priceEl = el.querySelector(SELECTORS.linePrice);
    if (priceEl) priceEl.textContent = this.formatMoney(item.final_line_price);

    var decreaseBtn = el.querySelector(SELECTORS.decrease);
    if (decreaseBtn) decreaseBtn.disabled = item.quantity <= 1;

    var removeBtn = el.querySelector(SELECTORS.remove);
    if (removeBtn) removeBtn.setAttribute('aria-label', 'Remove ' + (item.product_title || item.title) + ' from cart');
  };

  PremiumCart.prototype.updateHeaderBubble = function (count) {
    var bubbles = document.querySelectorAll('[data-cart-count]');
    Array.prototype.forEach.call(bubbles, function (el) {
      if (el.closest('[data-premium-cart]')) return;
      el.textContent = count;
      el.classList.add('is-bumped');
      window.setTimeout(function () {
        el.classList.remove('is-bumped');
      }, 220);
    });
  };

  PremiumCart.prototype.setBusy = function (isBusy) {
    if (this.checkoutBtn) this.checkoutBtn.setAttribute('aria-busy', isBusy ? 'true' : 'false');
  };

  PremiumCart.prototype.notifyError = function (message) {
    // Lightweight, non-blocking console + custom event; themes can hook a toast to this.
    console.error('[Premium Cart]', message);
    document.dispatchEvent(new CustomEvent('cart:error', { detail: { message: message } }));
  };

  /* ---------------------------------- Event binding ---------------------------------- */

  PremiumCart.prototype.bindEvents = function () {
    var self = this;
    this.boundKeydown = this.onKeydown.bind(this);

    this.overlay.addEventListener('click', function () {
      self.close();
    });

    Array.prototype.forEach.call(this.root.querySelectorAll(SELECTORS.close), function (btn) {
      btn.addEventListener('click', function () {
        self.close();
      });
    });

    this.root.addEventListener('keydown', function (e) {
      self.trapFocus(e);
    });

    this.itemsList.addEventListener('click', function (e) {
      self.onItemsClick(e);
    });

    this.checkoutBtn && this.checkoutBtn.addEventListener('click', function () {
      self.checkoutBtn.classList.add('is-loading');
    });

    // Global open triggers
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest(SELECTORS.openTrigger);
      if (trigger) {
        e.preventDefault();
        self.open();
      }
    });

    // Intercept any product "Add to cart" forms site-wide.
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (form.getAttribute('action') && form.getAttribute('action').indexOf('/cart/add') === -1) return;
      if (!form.querySelector('[name="id"]')) return;

      e.preventDefault();
      var formData = new FormData(form);
      self.addItem(formData);
    });

    // Support simple buttons with data-pc-add-to-cart + data-variant-id
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-pc-add-to-cart]');
      if (!btn) return;
      var variantId = btn.getAttribute('data-variant-id');
      if (!variantId) return;
      e.preventDefault();
      var quantity = parseInt(btn.getAttribute('data-quantity'), 10) || 1;
      var formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', quantity);
      self.addItem(formData);
    });
  };

  PremiumCart.prototype.onKeydown = function (e) {
    if (e.key === 'Escape') {
      this.close();
    }
  };

  PremiumCart.prototype.onItemsClick = function (e) {
    var itemEl = e.target.closest(SELECTORS.item);
    if (!itemEl) return;
    var key = itemEl.dataset.key;

    var removeBtn = e.target.closest(SELECTORS.remove);
    if (removeBtn) {
      this.changeLine(key, 0, itemEl);
      return;
    }

    var increaseBtn = e.target.closest(SELECTORS.increase);
    var decreaseBtn = e.target.closest(SELECTORS.decrease);
    if (increaseBtn || decreaseBtn) {
      var input = itemEl.querySelector(SELECTORS.qtyInput);
      var current = parseInt(input.value, 10) || 1;
      var next = increaseBtn ? current + 1 : Math.max(0, current - 1);
      input.value = next;
      if (next === 0) {
        this.changeLine(key, 0, itemEl);
      } else {
        this.queueChange(key, next, itemEl);
      }
    }
  };

  /* ---------------------------------- Init ---------------------------------- */

  function init() {
    var roots = document.querySelectorAll(SELECTORS.root);
    Array.prototype.forEach.call(roots, function (root) {
      if (root.__premiumCart) return;
      root.__premiumCart = new PremiumCart(root);
      window.PremiumCart = root.__premiumCart;
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();